import { useEffect, useMemo, useState } from "react"
import { Link, useLocation, useNavigate } from "react-router-dom"
import { useSession } from "../../auth/session"
import { useSelector } from "react-redux"
import { useRedemptions } from "../../redux/redemptionsSlice"
import RedeemCodeCard, { RedeemCodeState } from "../RedeemCodeCard"
import { RootState } from "../../redux/store"
import { Entitlement, EntitlementStatus, entitlementStatusLabels, License, LicenseType, licenseTypeLabels, Product, Redemption, Sku, Topup } from "../../api/models"
import Spinner from "../widgets/Spinner"
import CardImage from "../widgets/CardImage"
import SkuName from "../widgets/SkuName"
import { buildImageSrcSet } from "../widgets/imageSrcSet"
import { isPast, LicenseRows, ProductRow } from "./Library"
import { REWARD_POINTS_ENABLED } from "../../featureFlags"

// One card per redeemed code, laid out like a Licenses entry: the sku's
// screenshot and logo, the entitlement the code granted (version, type,
// expiry — or that it has since been traded in / revoked), the code itself,
// and what the code deposited: licenses as product rows, credit top-ups,
// reward points. Everything is read from the side-loaded entities the list
// fetch put in the store — no per-card fetching.

function formatCode(code: string): string {
  return code.match(/.{1,4}/g)?.join("-") ?? code
}

function formatDate(date: Date | string): string {
  return new Date(date).toLocaleDateString()
}

// A credit top-up row of a card: the product the credits are for, the
// amount, the expiry. Two renders like LicenseRows (stacked pairs on
// phones, a table otherwise); nothing to click through to.
function TopupRows({ topups, productsById }: { topups: Topup[], productsById: Map<string, Product> }) {
  const amount = (topup: Topup) => (topup.amount / 1e9).toFixed(2)
  const expiry = (topup: Topup) => topup.expiresAt ? formatDate(topup.expiresAt) : "Never"
  const name = (topup: Topup) => productsById.get(topup.productId)?.name ?? "unknown product"
  return (
    <>
      <div className="mobile-list">
        {topups.map(topup => (
          <div key={topup.id} className="mobile-list-item" style={{ cursor: "default" }}>
            <div className="mobile-list-kv">
              <span className="mobile-list-kv-label">Credits</span>
              <span className="mobile-list-kv-value">{amount(topup)} · {name(topup)}</span>
            </div>
            <div className="mobile-list-kv">
              <span className="mobile-list-kv-label">Expires</span>
              <span className="mobile-list-kv-value">{expiry(topup)}</span>
            </div>
          </div>
        ))}
      </div>
      <div className="table-scroll mobile-hidden">
        <table className="data-table">
          <thead>
            <tr>
              <th>Credits for</th>
              <th>Amount</th>
              <th>Expires</th>
            </tr>
          </thead>
          <tbody>
            {topups.map(topup => (
              <tr key={topup.id}>
                <td>{name(topup)}</td>
                <td>{amount(topup)}</td>
                <td>{expiry(topup)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  )
}

// The granted entitlement's expiry (code-granted rights never renew, so
// just Expires / Expired); a permanent one shows nothing
function RedemptionExpiry({ entitlement }: { entitlement: Entitlement }) {
  if (!entitlement.expiresAt) return null
  const expired = isPast(entitlement.expiresAt)
  return (
    <>
      <span className="library-meta-sep">·</span>
      <span className={expired ? "library-expiry-past" : "library-expiry-upcoming"}>
        {expired ? "Expired" : "Expires"} {formatDate(entitlement.expiresAt)}
      </span>
    </>
  )
}

interface CardData {
  redemption: Redemption
  sku?: Sku
  // absent on an older backend's response, or while the list is loading
  entitlement?: Entitlement
  rows: ProductRow[]
  topups: Topup[]
}

function RedemptionCard({ redemption, sku, entitlement, rows, topups, productsById }: CardData & { productsById: Map<string, Product> }) {
  const name = sku?.name ?? redemption.skuId
  // the badges mirror the Licenses header: no badge for standard, orange
  // for pre-release types; a non-active entitlement gets a neutral status
  // chip instead of an expiry line — the right it granted is gone
  const active = entitlement === undefined || entitlement.status === EntitlementStatus.Active
  const typeBadge = entitlement && entitlement.licenseType !== LicenseType.Standard ? licenseTypeLabels[entitlement.licenseType] : undefined
  const attention = entitlement?.licenseType === LicenseType.Alpha || entitlement?.licenseType === LicenseType.Beta
  const emptyText = entitlement?.status === EntitlementStatus.Superseded
    ? "The license this code granted was traded in for an upgrade."
    : entitlement?.status === EntitlementStatus.Revoked
      ? "The license this code granted has been revoked."
      : "This code didn't deposit anything on your account."
  return (
    <div className="license-group license-group-with-img">
      <div className="license-group-img-container">
        <CardImage imageUrl={sku?.imageUrl} availableWidths={sku?.availableWidths} alt={name} />
      </div>
      <div className="product-group-header">
        {sku?.imageUrl && (
          <img src={sku.imageUrl} srcSet={buildImageSrcSet(sku.imageUrl, sku.availableWidths)} sizes="44px" alt="" className="product-header-icon" />
        )}
        <h2 className="section-title library-entry-title">
          {sku ? <SkuName sku={sku} variant="section" /> : name}
          {entitlement && <span className="library-version">v{entitlement.skuVersion}</span>}
          {typeBadge && <span className={`badge-stage${attention ? " badge-stage-attention" : ""}`}>{typeBadge}</span>}
          {entitlement && !active && <span className="badge-pending">{entitlementStatusLabels[entitlement.status]}</span>}
        </h2>
        <div className="redemption-card-actions">
          <Link to={`/redemptions/${redemption.id}`} className="card-link">Details</Link>
        </div>
      </div>
      <div className="library-entry-body">
        <div className="library-entry-meta">
          <span className="redemption-card-code">{formatCode(redemption.code)}</span>
          <span className="library-meta-sep">·</span>
          {redemption.redeemedAt ? `Redeemed ${formatDate(redemption.redeemedAt)}` : "Not yet redeemed"}
          {entitlement && active && <RedemptionExpiry entitlement={entitlement} />}
          {REWARD_POINTS_ENABLED && redemption.rewardPoints > 0 && (
            <>
              <span className="library-meta-sep">·</span>
              +{redemption.rewardPoints} reward points
            </>
          )}
          {/* issued iLok-disabled (the batch's switch) — the same note the
              Licenses card shows in place of the iLok link */}
          {entitlement?.ilokEnabled === false && active && (
            <>
              <span className="library-meta-sep">·</span>
              <span className="library-muted" style={{ cursor: "help" }} title="This license does not allow iLok counterpart to be issued">iLok not enabled</span>
            </>
          )}
        </div>
        {rows.length > 0 && <LicenseRows rows={rows} />}
        {topups.length > 0 && <TopupRows topups={topups} productsById={productsById} />}
        {rows.length === 0 && topups.length === 0 && (
          <p className="empty-state library-muted">{emptyText}</p>
        )}
      </div>
    </div>
  )
}

export default function Redemptions() {
  const session = useSession()
  const accessToken = session.accessToken ?? ""
  const { ids, haveMore, loading, more } = useRedemptions(accessToken)
  const location = useLocation()
  const navigate = useNavigate()

  // A code handed over by another page (the Dashboard card's drop zone) is
  // taken from the router state once and the state cleared, so a reload or
  // back-navigation doesn't refill the form.
  const [initialCode, setInitialCode] = useState<string | undefined>(undefined)
  useEffect(() => {
    const code = (location.state as RedeemCodeState | null)?.redeemCode
    if (!code) return
    setInitialCode(code)
    navigate(location.pathname, { replace: true, state: null })
  }, [location.state, location.pathname, navigate])
  const entities = useSelector((state: RootState) => state.redemptions.entities)
  const entitlements = useSelector((state: RootState) => state.redemptions.entitlements)
  const skus = useSelector((state: RootState) => state.skus.entities)
  const licenses = useSelector((state: RootState) => state.licenses.entities)
  const products = useSelector((state: RootState) => state.products.entities)
  const topups = useSelector((state: RootState) => state.topups.entities)

  // the cards' data, resolved once from the side-loaded entities (the list
  // and redeem responses fill every slice read here)
  const { cards, productsById } = useMemo(() => {
    const productsById = new Map<string, Product>()
    for (const entity of Object.values(products))
      if (entity?.data) productsById.set(entity.data.id, entity.data)
    const cards: CardData[] = (ids ?? [])
      .map(id => entities[id]?.data)
      .filter((redemption): redemption is Redemption => redemption != null)
      .map(redemption => {
        const rowLicenses = redemption.licenseIds
          .map(id => licenses[id]?.data)
          .filter((license): license is License => license != null)
        return {
          redemption,
          sku: skus[redemption.skuId]?.data,
          entitlement: redemption.entitlementIds?.map(id => entitlements[id]).find(it => it != null),
          rows: rowLicenses.map(license => ({ product: productsById.get(license.productId), license })),
          topups: redemption.topupIds
            .map(id => topups[id]?.data)
            .filter((topup): topup is Topup => topup != null)
        }
      })
    return { cards, productsById }
  }, [ids, entities, entitlements, skus, licenses, products, topups])

  const isLoading = ids === undefined

  return (
    <div className="page">
      <h1 className="page-title">License Codes</h1>

      <RedeemCodeCard initialCode={initialCode} />

      <h2 className="section-title">Past redemptions</h2>

      {(isLoading || loading) && cards.length === 0 && (
        <p className="empty-state"><Spinner size={16} /> Loading redemptions…</p>
      )}

      {!isLoading && !loading && cards.length === 0 && (
        <div className="empty-state-block">
          <span className="empty-state-icon">&#128273;</span>
          <span>No redemptions yet</span>
          <span className="empty-state-hint">Redeem your first code above</span>
        </div>
      )}

      {cards.map(card => (
        <RedemptionCard key={card.redemption.id} {...card} productsById={productsById} />
      ))}

      <div style={{ marginLeft: "8px" }}>
        {haveMore && ids !== undefined && (
          loading ? <Spinner size={16} /> : (
            <button className="card-link" onClick={more} style={{ background: "none", border: "none", cursor: "pointer" }}>
              Load more
            </button>
          )
        )}
      </div>
    </div>
  )
}
